import { Modal } from "semantic-ui-react";

import { createFormHelper } from "lib/Form";
import { SEASONS } from "models";
import { TyreForm } from "modules/TyresDashboard/components/TyreModal/components";
import { useBrandsQuery, useCreateTyreMutation, useUpdateTyreMutation } from "modules/TyresDashboard/components/TyreModal/hooks";
import "modules/TyresDashboard/components/TyreModal/TyreModal.scss";
import { EAN_VALIDATION, isKnownManufacturer } from "utils/common";

import type { DropdownItemProps } from "components";
import type { Tyre } from "models";
import type { TyreFormValues } from "modules/TyresDashboard/components/TyreModal/components";
import type { TyreFormPayload } from "modules/TyresDashboard/components/TyreModal/hooks";

type TyreModalProps = {
  tyre?: Tyre | null;
  onClose: () => void;
};

const FormHelper = createFormHelper<TyreFormValues>();

const isFormInvalid = ({ manufacturer, description, speed, width, height, size, carry_capacity, ean }: TyreFormValues, brandOptions: DropdownItemProps<string>[]) =>
  !isKnownManufacturer(brandOptions, manufacturer) || !description || !speed || !width || !height || !size || !carry_capacity || !EAN_VALIDATION.test(ean);

const getTyrePayload = ({ width, height, size, carry_capacity, ...rest }: TyreFormValues): TyreFormPayload => ({
  ...rest,
  width: width ?? 0,
  height: height ?? 0,
  size: size ?? 0,
  carry_capacity: carry_capacity ?? 0
});

export const TyreModal = ({ tyre, onClose }: TyreModalProps) => {
  const { data: brandOptions = [] } = useBrandsQuery();
  const { mutateAsync: createTyre, isPending: isCreateTyrePending } = useCreateTyreMutation();
  const { mutateAsync: updateTyre, isPending: isUpdateTyrePending } = useUpdateTyreMutation();

  const handleSubmit = async (values: TyreFormValues) => {
    if (isFormInvalid(values, brandOptions)) return;

    const payload = getTyrePayload(values);

    if (tyre) await updateTyre(payload);
    else await createTyre(payload);

    onClose();
  };

  return (
    <Modal open={tyre !== undefined} onClose={onClose} closeOnDimmerClick={false} size="large">
      <div className="TyreModal">
        <FormHelper.Form
          onSubmit={handleSubmit}
          defaultValues={{
            id: tyre?.id ?? 0,
            manufacturer: tyre?.manufacturer ?? "",
            description: tyre?.description ?? "",
            carry_capacity: tyre?.carry_capacity ?? null,
            width: tyre?.width ?? null,
            height: tyre?.height ?? null,
            size: tyre?.size ?? null,
            speed: tyre?.speed ?? "",
            season: tyre?.season ?? SEASONS.AllSeason,
            ean: tyre?.ean ?? "",
            run_flat_tire: tyre?.run_flat_tire ?? false
          }}
        >
          <TyreForm isEdit={tyre !== null} isPending={isCreateTyrePending || isUpdateTyrePending} brandOptions={brandOptions} onClose={onClose} />
        </FormHelper.Form>
      </div>
    </Modal>
  );
};
